import { PublicationContext, Subscription } from 'centrifuge';
import { computed, nextTick, reactive, ref } from 'vue';

import { ChatsApi } from 'SharedDir/backend/api';
import useCentrifuge from 'SharedDir/common/composables/useCentrifuge';
import { ChatEvents } from 'SharedDir/constants/chat';
import { UserInfo } from 'SharedDir/declarations/access';
import { Chats } from 'SharedDir/declarations/chats';
import Thread from 'SharedDir/modules/Chats/Thread.vue';
import ChatMessage from 'SharedDir/service/chats/classes/chat-message';
import { useUserStore } from 'SharedDir/stores/user.store';
import { camelizeObject } from 'SharedDir/utils/helpers';

export interface MessengerPublicationContext extends PublicationContext {
  data: {
    payload:
    | Chats.NewChatNotification
    | Chats.NewMessageNotification
    | Chats.DeleteMessageNotification;
  };
}

export default () => {
  const { centrifuge, centrifugeReady } = useCentrifuge();
  const user = useUserStore().getUser as UserInfo;
  let rawChats = reactive<Chats.Chat[]>([]);

  let subscription: Subscription | null = null;

  const refThread = ref<typeof Thread>(null as any);
  const currentChat = ref<null | Chats.Chat>(null);
  const currentChatMessages = ref<Array<ChatMessage>>([]);
  const newCurrentChatMessageReceiveHook = ref((): void => {});
  const chats = computed(() => rawChats);
  const unreadMessagesCount = computed(() => {
    return rawChats.filter((chat) => !chat.isMute).reduce((a, b) => a + (b.unreadMessages || 0), 0);
  });

  async function init() {
    const { data } = await ChatsApi.getChats({
      sortBy: 'latestMessageSentAt',
      descending: true,
      participantId: user.id,
    });
    rawChats = data.map((c) => ({ ...c, unreadMessages: c.unreadMessagesCount }));
    await centrifugeReady();
    subscription = centrifuge.newSubscription(`chat_personal_notification..#${user.id}`);
    subscription
      .on('publication', (context: MessengerPublicationContext) => {
        const payload:
        | Chats.NewChatNotification
        | Chats.NewMessageNotification
        | Chats.DeleteMessageNotification = camelizeObject(context.data.payload);
        const {
          event,
          eventData: {
            chat: { id: chatId },
          },
        } = payload;
        if (event === ChatEvents.ChatMessageCreated) {
          receiveMessage(chatId, payload.eventData.message);
        } else if (event === ChatEvents.ChatCreated) {
          receiveNewChat(chatId);
        } else if (event === ChatEvents.ChatMessageDeleted) {
          const messageId = payload.eventData.message.id;
          removeChatMessage(chatId, messageId);
        }
      })
      .subscribe();
    console.debug(subscription);
  }

  async function toggleMute(chat: Chats.Chat) {
    await ChatsApi.toggleMute(chat.id);
  }

  async function togglePin(chat: Chats.Chat) {
    await ChatsApi.togglePin(chat.id);
    const index = rawChats.findIndex((item) => item.id === chat.id);
    rawChats[index].isFavorite = !rawChats[index].isFavorite;
  }

  function setCurrentChat(chat: Chats.Chat) {
    console.debug('Setting current chat: ', chat);
    currentChat.value = chat;
    currentChat.value.unreadMessages = 0;
    currentChatMessages.value = [];
  }

  async function moreMessages(index: number, done: (stop?: boolean) => void) {
    if (!currentChat.value) {
      done(true);
      return;
    }
    const offset = currentChatMessages.value.length;
    console.debug('Loading more messages ...');
    const { data: loadedMessages, limit } = await ChatsApi.getMessages(currentChat.value.id, {
      offset,
    });
    currentChatMessages.value.unshift(...fixMessages(loadedMessages).reverse());
    done(loadedMessages.length < limit);
  }

  async function moreChats(index: number, done: (stop?: boolean) => void) {
    const offset = rawChats.length;
    const { data: loadedMessages, limit } = await ChatsApi.getChats({
      offset,
      sortBy: 'latestMessageSentAt',
      participantId: user.id,
    });
    rawChats = rawChats.concat(loadedMessages);
    done(loadedMessages.length < limit);
  }

  async function setCurrent(chat: Chats.Chat) {
    setCurrentChat(chat);
    await nextTick();
    await refThread.value.reset();
  }

  async function send(message: Chats.CreateMessage, resolve: () => void, reject: (e: any) => void) {
    if (!currentChat.value) {
      return;
    }
    try {
      await ChatsApi.sendMessage(currentChat.value.id, message);
      resolve();
    } catch (e) {
      reject(e);
      throw e;
    }
  }

  function fixMessages(messages: Array<Chats.Message>): Array<ChatMessage> {
    return messages.map((m) => fixMessage(m));
  }

  function fixMessage(m: Chats.Message): ChatMessage {
    return new ChatMessage(m);
  }

  async function remove(message: Chats.ChatMessage | ChatMessage) {
    if (!currentChat.value) {
      return;
    }
    await ChatsApi.deleteMessage(currentChat.value.id, message.id);
    const index = currentChatMessages.value.findIndex((m) => m.id === message.id);
    if (index !== -1) {
      currentChatMessages.value.splice(index, 1);
    }
  }

  async function receiveChat(chatId: number): Promise<Chats.Chat> {
    const chat = await ChatsApi.getChat(chatId.toString());

    // Убираем чат из списка, чтобы не было дубля
    const chatInRawIndex = rawChats.findIndex(({ id }) => id === chat.id);
    if (chatInRawIndex !== -1) {
      rawChats.splice(chatInRawIndex, 1);
    }

    rawChats.unshift(chat);

    return chat;
  }

  async function receiveNewChat(chatId: number): Promise<Chats.Chat> {
    const chat = await ChatsApi.getChat(chatId.toString());

    // Убираем чат из списка, чтобы не было дубля
    const chatInRawIndex = rawChats.findIndex(({ id }) => id === chat.id);
    if (chatInRawIndex !== -1) {
      rawChats.splice(chatInRawIndex, 1);
    }

    const lastReadChatIndex = rawChats.findIndex(({ unreadMessages }) => unreadMessages === 0);

    if (lastReadChatIndex !== -1) {
      rawChats.splice(lastReadChatIndex, 0, chat);
    } else {
      rawChats.unshift(chat);
    }

    return chat;
  }

  async function receiveMessage(chatId: Chats.Chat['id'], message: Chats.Message) {
    const index = rawChats.findIndex((c) => c.id === chatId);
    let chat: Chats.Chat;
    if (index !== -1) {
      chat = pullChatUp(index);
      chat.messages.unshift(message);
      if (!currentChat.value || currentChat.value.id !== chat.id) {
        chat.unreadMessages += 1;
      }
    } else {
      const c = await receiveChat(chatId);
      c.unreadMessages = 1;
    }
    const fixedMessage: ChatMessage = fixMessage(message);
    const exists = currentChatMessages.value.find(({ id }) => id === fixedMessage.id);
    if (currentChat.value && currentChat.value.id === chatId && !exists) {
      currentChatMessages.value.push(fixedMessage);
      console.debug(currentChatMessages.value);
      newCurrentChatMessageReceiveHook.value();
    }

    if (user.id === fixedMessage.id) {
      await refThread.value.scrollToBottom();
    }
  }

  function pullChatUp(index: number): Chats.Chat {
    const existing = rawChats[index];
    if (index !== 0) {
      rawChats.splice(index, 1);
      rawChats.unshift(existing);
    }
    return existing;
  }

  function shutdown() {
    Object.values(centrifuge.subscriptions()).forEach((s) => {
      s.unsubscribe();
      centrifuge.removeSubscription(s);
    });
  }

  function removeChatMessage(chatId: number, messageId: number): void {
    const chatIndex = rawChats.findIndex((c) => c.id === chatId);
    if (chatIndex !== -1) {
      removeMessage(rawChats[chatIndex].messages, messageId);
    }

    if (currentChat.value && currentChat.value.id === chatId) {
      removeMessage(currentChatMessages.value, messageId);
    }
  }
  function removeMessage(list: Array<ChatMessage | Chats.Message>, messageId: number) {
    const index = list.findIndex((m) => m.id === messageId);
    if (index !== -1) {
      list.splice(index, 1);
    }
  }

  return {
    init,
    toggleMute,
    togglePin,
    currentChat,
    currentChatMessages,
    newCurrentChatMessageReceiveHook,
    unreadMessagesCount,
    chats,
    setCurrentChat,
    setCurrent,
    moreMessages,
    moreChats,
    send,
    remove,
    receiveMessage,
    receiveChat,
    refThread,
    shutdown,
  };
};
